import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Sun, Moon, Phone, Languages, ChevronDown, Sparkles, Globe2 } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { CLINIC, waLink } from "../../lib/clinic";
import { useLang } from "../../lib/i18n";

const linksBuilder = (t) => [
  { to: "/", label: t("nav.home"), key: "home" },
  {
    to: "/about",
    label: t("nav.about"),
    key: "about",
    // Sub-menu: switching to a two-consultant team.
    children: [
      { to: "/about", label: "Dr. Shantanu Kumar Gupta", role: "Cataract · Cornea · Retina" },
      { to: "/team/dr-manisha-mishra", label: "Dr. Manisha Mishra", role: "Cataract · Squint · Pediatric" },
      { to: "/international-patients", label: "International Patients", role: "English consultations · digital reports · WhatsApp follow-up" },
    ],
  },
  {
    to: "/expertise",
    label: t("nav.expertise"),
    key: "expertise",
    // Short labels for the dropdown so rows don't wrap and overlap on desktop.
    children: [
      { to: "/premium-cataract-surgery-in-varanasi", label: "Cataract Surgery" },
      { to: "/complex-cataract-surgery-in-varanasi", label: "Complex Cataract Surgery" },
      { to: "/medical-retina-specialist-in-varanasi", label: "Retina & Injections" },
      { to: "/squint-surgery-in-varanasi", label: "Squint (Strabismus) Surgery" },
      { to: "/pediatric-ophthalmologist-in-varanasi", label: "Pediatric Ophthalmology" },
      { to: "/rop-screening-in-varanasi", label: "ROP Screening" },
      { to: "/comprehensive-eye-examination-in-varanasi", label: "Comprehensive Eye Exam" },
      { to: "/diabetic-retinopathy-treatment-in-varanasi", label: "Diabetic Retinopathy" },
      { to: "/glaucoma-treatment-in-varanasi", label: "Glaucoma" },
      { to: "/iol-selector", label: "IOL Lens Selector Tool", role: "Interactive · pick the right lens in 3-5 min" },
      { to: "/symptom-checker", label: "AI Eye Symptom Checker", role: "60-second educational triage · red-flag rules" },
      { to: "/best-cataract-surgeon-in-varanasi", label: "Best Cataract Surgeon in Varanasi" },
      { to: "/best-retina-specialist-in-varanasi", label: "Best Retina Specialist in Varanasi" },
      { to: "/best-eye-doctor-varanasi", label: "Best Eye Doctor in Varanasi (Guide)" },
      { to: "/cataract-surgery-cost-in-varanasi", label: "Cataract Surgery Cost" },
      { to: "/expertise", label: "All clinical detail pages →", divider: true },
    ],
  },
  { to: "/content-hub", label: t("nav.content_hub"), key: "content-hub" },
  { to: "/blog", label: t("nav.blog"), key: "blog" },
  { to: "/guides", label: t("nav.guides"), key: "guides" },
  {
    to: "/videos",
    label: "Media",
    key: "media",
    // Group all video / press assets under one navbar entry so the
    // /videos hub is discoverable without cluttering the top-level nav.
    children: [
      { to: "/videos", label: "Videos & Reels", role: "Shorts, testimonials, talks" },
      { to: "/eye-cases", label: "Eye Case Studies", role: "Slit lamp · Fundus · OCT · FFA cases" },
      { to: "/publications", label: "Publications", role: "Peer-reviewed research · ResearchGate" },
      { to: "/media", label: "Press & Interviews", role: "News articles · podcasts · talks" },
      { to: "/press", label: "Press Kit", role: "Bios · portraits · quotable stats for journalists" },
    ],
  },
  { to: "/faq", label: t("nav.faq"), key: "faq" },
  { to: "/contact", label: t("nav.contact"), key: "contact" },
];

const Navbar = () => {
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const links = linksBuilder(t);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Auto-close the mobile menu when the route changes so tapping a link
  // (which navigates the SPA) doesn't leave the drawer open on the next page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  // Freeze background scroll while the mobile menu is open so the drawer's
  // internal scroll owns touch gestures cleanly and there's no dual-scroll.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 glass-nav transition-all ${scrolled ? "shadow-sm" : ""}`}
    >
      <div className="container-nav flex items-center justify-between gap-3 h-20">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="nav-logo">
          <div className="w-10 h-10 shrink-0 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center relative">
            <span className="absolute w-4 h-4 rounded-full bg-[hsl(var(--accent))]" />
            <span className="absolute w-1.5 h-1.5 rounded-full bg-white" />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-base md:text-lg xl:text-xl text-[hsl(var(--foreground))] whitespace-nowrap">Dr. Shantanu Kumar Gupta</span>
            <span className="hidden min-[1700px]:block text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] whitespace-nowrap">
              Cataract · Cornea · Medical Retina · Varanasi
            </span>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-3 2xl:gap-5">
          {links.map((l) =>
            l.children ? (
              <div
                key={l.to}
                className="relative group"
                data-testid={`nav-${l.key}-wrap`}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-${l.key}`}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1 text-sm tracking-wide transition-colors whitespace-nowrap ${
                      isActive
                        ? "text-[hsl(var(--primary))] font-medium"
                        : "text-[hsl(var(--foreground))]/75 hover:text-[hsl(var(--primary))]"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                  <ChevronDown size={13} strokeWidth={1.75} className="opacity-70 group-hover:rotate-180 transition-transform" />
                </NavLink>
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition-opacity"
                  data-testid={`nav-${l.key}-dropdown`}
                >
                  <div className={`${l.key === "expertise" ? "min-w-[360px]" : "min-w-[280px]"} rounded-2xl border border-border bg-background shadow-2xl shadow-black/25 dark:shadow-black/60 p-2`}>
                    {l.children.map((c) => (
                      c.divider ? (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block mt-1 pt-3 border-t border-border/60 px-3 py-2.5 text-xs uppercase tracking-[0.16em] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 rounded-lg"
                          data-testid={`nav-${l.key}-child-${c.to.replace(/\W+/g, "-")}`}
                        >
                          {c.label}
                        </Link>
                      ) : (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block px-3 py-2.5 rounded-lg hover:bg-[hsl(var(--primary))]/5 text-sm leading-snug transition-colors"
                          data-testid={`nav-${l.key}-child-${c.to.replace(/\W+/g, "-")}`}
                        >
                          <div className="font-medium text-[hsl(var(--foreground))]">{c.label}</div>
                          {c.role && (
                            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] mt-1">
                              {c.role}
                            </div>
                          )}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.key}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors whitespace-nowrap ${
                    isActive
                      ? "text-[hsl(var(--primary))] font-medium"
                      : "text-[hsl(var(--foreground))]/75 hover:text-[hsl(var(--primary))]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden sm:flex items-center gap-1.5">
            <button
              aria-label={t("lang.aria")}
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "hi" ? "en" : "hi")}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] h-10 px-3 rounded-full border border-border text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))] transition-colors"
              title={t("lang.aria")}
            >
              <Languages size={14} strokeWidth={1.5} />
              {t("lang.toggle")}
            </button>
            {/* AI-translation disclosure — shown only when Hindi is active.
                Hover / tap reveals a short tooltip so patients understand
                machine translation may occasionally sound formal. */}
            {lang === "hi" && (
              <div className="relative group" data-testid="ai-translate-badge">
                <div
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-[hsl(var(--accent))]/12 text-[9px] uppercase tracking-[0.16em] text-[hsl(var(--accent))] cursor-help select-none"
                  aria-describedby="ai-translate-tip"
                  tabIndex={0}
                >
                  <Sparkles size={9} strokeWidth={2} aria-hidden="true" />
                  <span>AI</span>
                </div>
                <div
                  id="ai-translate-tip"
                  role="tooltip"
                  className="pointer-events-none absolute right-0 top-full mt-2 w-64 rounded-xl border border-border bg-background p-3 text-[11px] leading-relaxed text-[hsl(var(--foreground))]/80 opacity-0 translate-y-1 group-hover:opacity-100 group-focus-within:opacity-100 group-hover:translate-y-0 group-focus-within:translate-y-0 transition-all shadow-lg z-50"
                >
                  <div className="font-medium text-[hsl(var(--foreground))] mb-0.5">
                    यह पृष्ठ AI द्वारा अनुवादित है
                  </div>
                  <div className="text-[hsl(var(--muted-foreground))]">
                    Content on this page is auto-translated to Hindi using AI. Medical terms and proper nouns are kept in English on purpose. Original English version is one tap away.
                  </div>
                </div>
              </div>
            )}
          </div>
          <button
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="theme-toggle"
            onClick={toggle}
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--primary))] transition-colors"
          >
            {theme === "dark" ? <Sun size={18} strokeWidth={1.5} aria-hidden="true" /> : <Moon size={18} strokeWidth={1.5} aria-hidden="true" />}
          </button>
          <Link
            to="/international-patients"
            data-testid="nav-intl-pill"
            className="hidden xl:inline-flex items-center justify-center w-10 h-10 rounded-full border border-border text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))] transition-colors"
            title="International patients"
            aria-label="International patients"
          >
            <Globe2 size={16} strokeWidth={1.5} aria-hidden="true" />
          </Link>
          <Link
            to="/iol-selector"
            data-testid="nav-iol-pill"
            className="hidden 2xl:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3.5 py-2 rounded-full border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10 transition-colors"
            title="Interactive IOL Selection Suite"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))]" />
            IOL Selector
          </Link>
          <a
            href={waLink("Hello Dr. Shantanu, I'd like to book an appointment.")}
            target="_blank"
            rel="nofollow noopener noreferrer"
            data-testid="nav-book-btn"
            className="hidden md:inline-flex btn-primary !py-2.5 !px-5 text-xs"
          >
            {t("cta.book")}
          </a>
          <button
            className="xl:hidden w-10 h-10 flex items-center justify-center rounded-full border border-border"
            aria-label="Menu"
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="xl:hidden border-t border-border bg-background/95 backdrop-blur-xl overflow-y-auto overscroll-contain"
          // The header is `sticky top-0` so the menu inherits sticky positioning
          // and cannot scroll natively. Cap its height at the viewport minus the
          // 80px navbar height (uses `dvh` so mobile browser chrome doesn't clip
          // the last item behind the URL/address bar), then let the inner list
          // scroll on overflow.
          style={{ maxHeight: "calc(100dvh - 80px)", WebkitOverflowScrolling: "touch" }}
          data-testid="mobile-menu"
        >
          <div className="container-page py-6 pb-10 flex flex-col gap-4">
            {links.map((l) => (
              <div key={l.to}>
                <NavLink
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-base block ${isActive ? "text-[hsl(var(--primary))] font-medium" : "text-[hsl(var(--foreground))]/80"}`
                  }
                  data-testid={`mobile-nav-${l.key}`}
                >
                  {l.label}
                </NavLink>
                {l.children && (
                  <div className="mt-2 ml-4 pl-3 border-l border-border/60 space-y-2">
                    {l.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-${l.key}-child-${c.to.replace(/\W+/g, "-")}`}
                        className={`block ${
                          c.divider
                            ? "text-xs uppercase tracking-[0.16em] text-[hsl(var(--primary))] pt-2 mt-2 border-t border-border/40"
                            : "text-sm text-[hsl(var(--foreground))]/70"
                        }`}
                      >
                        {c.label}
                        {c.role && (
                          <span className="block text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--accent))] mt-0.5">
                            {c.role}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button
              onClick={() => { setLang(lang === "hi" ? "en" : "hi"); setOpen(false); }}
              className="btn-ghost text-xs justify-start"
              data-testid="mobile-lang-toggle"
            >
              <Languages size={14} className="mr-1" /> {t("lang.toggle")}
              {lang === "hi" && (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[hsl(var(--accent))]/12 text-[8px] uppercase tracking-[0.16em] text-[hsl(var(--accent))]" data-testid="mobile-ai-badge">
                  <Sparkles size={8} strokeWidth={2} /> AI translated
                </span>
              )}
            </button>
            <a href={waLink()} target="_blank" rel="nofollow noopener noreferrer" className="btn-primary mt-2" data-testid="mobile-book-btn">
              {t("cta.book")}
            </a>
            <a href={`tel:${CLINIC.phoneRaw}`} className="btn-ghost text-xs" data-testid="mobile-call-btn">
              <Phone size={14} className="mr-1" /> {CLINIC.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
